import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const dayKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`; };

export const useDashboardData = ({ query, selectedDate, enabled = true, role = "admin" }) => {
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    const canRequests = role !== "doctor";
    try {
      const [d, ap, st, rq] = await Promise.all([
        api.get("/doctors"), api.get("/appointments"), api.get("/stats"),
        canRequests ? api.get("/booking-requests") : Promise.resolve({ data: [] }),
      ]);
      setDoctors(d.data); setAppointments(ap.data); setStats(st.data); setRequests(rq.data);
    } catch (e) { toast.error(apiError(e, "Failed to load dashboard data")); }
  }, [role]);

  useEffect(() => { if (enabled) load(); }, [load, enabled]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return appointments.filter((a) =>
      [a.patient_name, a.doctor_name, a.disease_name || "", a.receipt_no].some((v) => v.toLowerCase().includes(q)));
  }, [appointments, query]);

  const newReqCount = useMemo(() => requests.filter((r) => r.status === "new").length, [requests]);
  const pendingCount = useMemo(() => appointments.filter((a) => a.status === "pending").length, [appointments]);
  const badges = useMemo(() => ({ requests: newReqCount, appointments: pendingCount }), [newReqCount, pendingCount]);
  const apptDays = useMemo(() => appointments.map((a) => new Date(a.appointment_date)).filter((d) => !isNaN(d)), [appointments]);
  const dayAppointments = useMemo(() => appointments
    .filter((a) => dayKey(a.appointment_date) === dayKey(selectedDate))
    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date)), [appointments, selectedDate]);

  const run = useCallback(async (fn, okMsg, errMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(apiError(e, errMsg)); }
  }, [load]);

  return { doctors, appointments, requests, stats, load, run, filtered, newReqCount, pendingCount, badges, apptDays, dayAppointments };
};
